import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { ReportingWebhookResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, SHARED_QUERY_OPTIONS } from './constants';
import { filterReportingWebhooks, mapReportingWebhookToSearchResult } from './utils/form-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseWebhooksSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type WebhooksSource = SearchSource<ReportingWebhookResult>;

export function useWebhooksSource({ workspaceId, enabled, query }: UseWebhooksSourceOptions): WebhooksSource {
  const webhooksQuery = useQuery(
    getServicesQueryOptions(
      'getReportingWebhooks',
      { params: { environment_id: workspaceId } },
      {
        enabled,
        ...SHARED_QUERY_OPTIONS,
        select: (data) => data.webhooks,
      },
    ),
  );

  const results = useMemo(
    () =>
      filterReportingWebhooks(webhooksQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((webhook) =>
        mapReportingWebhookToSearchResult(webhook, workspaceId),
      ),
    [webhooksQuery.data, query, workspaceId],
  );

  return { ...webhooksQuery, results };
}
